import { Box, Flex, Text, useDisclosure } from "@chakra-ui/react";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { format } from "date-fns";
import { DayPicker, Matcher } from "react-day-picker";
import { ReactNode, useMemo } from "react";
import { fr } from "react-day-picker/locale";

type InputSingleDatePickerProps = {
  date?: Date;
  setDate?: (date?: Date) => void;
  placeholder?: ReactNode | string;
  disableStrategy?: () => Matcher | Matcher[] | undefined;
};

const InputSingleDatePicker = ({
  date,
  setDate,
  placeholder,
  disableStrategy,
}: InputSingleDatePickerProps) => {
  const disclosure = useDisclosure();
  const {
    refs,
    floatingStyles: style,
    context,
  } = useFloating({
    open: disclosure.isOpen,
    placement: "bottom",
    onOpenChange: (open: boolean) =>
      open ? disclosure.onOpen() : disclosure.onClose(),
    middleware: [offset(10), shift(), flip()],
    whileElementsMounted: autoUpdate,
  });
  // const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: "tooltip",
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    focus,
    dismiss,
    role,
  ]);
  const disabled = useMemo(() => {
    if (disableStrategy) {
      return disableStrategy();
    }
    return undefined;
  }, [disableStrategy]);
  const handleSetDate = (date?: Date) => {
    if (setDate) {
      setDate(date);
    }
    disclosure.onClose();
  };
  return (
    <Box
      position="relative"
      w="100%"
      h="2.5rem"
      border="solid 1px"
      borderRadius="xl"
      borderColor="gray.200"
      onClick={() => disclosure.onOpen()}
      ref={refs.setReference}
      {...getReferenceProps}
      cursor="pointer"
    >
      {placeholder && (
        <Flex
          position="absolute"
          top={disclosure.isOpen || date !== undefined ? -3 : 2}
          left={disclosure.isOpen || date !== undefined ? 2 : 4}
          bgColor={
            disclosure.isOpen || date !== undefined ? "white" : undefined
          }
          px={disclosure.isOpen || date !== undefined ? 2 : undefined}
          transition="all 200ms ease-out"
          color="gray.500"
          alignItems="center"
        >
          {placeholder}
        </Flex>
      )}
      {date !== undefined && (
        <Text position="absolute" top={2} left={4} zIndex={1}>
          {format(date, "dd/MM/yyyy")}
        </Text>
      )}
      {disclosure.isOpen && (
        <Box
          ref={refs.setFloating}
          style={style}
          {...getFloatingProps()}
          bgColor="white"
          zIndex={1000}
          border="solid 1px"
          borderColor="gray.200"
          borderRadius="xl"
          boxShadow="lg"
          p={4}
        >
          <DayPicker
            locale={fr}
            mode="single"
            selected={date}
            onSelect={handleSetDate}
            disabled={disabled}
          />
        </Box>
      )}
    </Box>
  );
};

export default InputSingleDatePicker;
